import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tag, X as CloseIcon } from "lucide-react";
import type { ContactGroupSummary } from "@multi-chat/shared";
import { Button } from "../../../components/ui/button/index.js";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../../../components/ui/sheet/index.js";
import { cn } from "../../../lib/utils.js";
import {
  CONTACT_SHEET_OVERLAY_MOTION_CLASS,
} from "../contacts-table-presentation.js";
import {
  contactSheetDescriptionClass,
  contactSheetHeaderClass,
  contactSheetHeaderIconClass,
  contactSheetTitleClass,
} from "../contact-sheet-presentation.js";
import { ContactsGroupsPanel } from "./ContactsGroupsPanel.js";

export function ContactsMobileGroupsSheet({
  open,
  groups,
  selectedGroupId,
  onClose,
  onSelectAllContacts,
  onSelectGroup,
  onCreateGroup,
  onRenameGroup,
  onDeleteGroup,
  onDeleteAllContacts,
  allContactsCount,
}: {
  open: boolean;
  groups: ContactGroupSummary[];
  selectedGroupId: number | null;
  onClose: () => void;
  onSelectAllContacts: () => void;
  onSelectGroup: (groupId: number) => void;
  onCreateGroup: (name: string) => Promise<void>;
  onRenameGroup: (groupId: number, name: string) => Promise<void>;
  onDeleteGroup: (groupId: number) => Promise<void>;
  onDeleteAllContacts: (group: ContactGroupSummary) => void;
  allContactsCount: number;
}): React.JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  useEffect(() => {
    if (!open) {
      setGroupFormOpen(false);
      setNewGroupName("");
    }
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()} modal={false}>
      <SheetContent
        side="start"
        className="flex h-full max-h-dvh w-[min(100%,22rem)] flex-col gap-0 overflow-hidden border-e border-border/60 bg-surface p-0 text-foreground shadow-lg lg:w-[20rem]"
        overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header className={contactSheetHeaderClass}>
          <div className="flex min-w-0 items-start gap-3">
            <span className={contactSheetHeaderIconClass} aria-hidden="true">
              <Tag size={16} />
            </span>
            <div className="grid min-w-0 gap-1">
              <SheetTitle
                id={titleId}
                className={cn(
                  contactSheetTitleClass,
                  "text-[1.0625rem] leading-tight tracking-[-0.02em]",
                )}
              >
                {t("contacts.mobile.groupsTitle")}
              </SheetTitle>
              <SheetDescription id={descriptionId} className={contactSheetDescriptionClass}>
                {t("contacts.mobile.groupsDescription")}
              </SheetDescription>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <CloseIcon className="size-4" aria-hidden="true" />
          </Button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
          <ContactsGroupsPanel
            variant="drawer"
            allContactsCount={allContactsCount}
            groups={groups}
            selectedGroupId={selectedGroupId}
            groupFormOpen={groupFormOpen}
            newGroupName={newGroupName}
            onNewGroupNameChange={setNewGroupName}
            onOpenGroupForm={() => setGroupFormOpen(true)}
            onCloseGroupForm={() => {
              setGroupFormOpen(false);
              setNewGroupName("");
            }}
            onCreateGroup={async () => {
              const name = newGroupName.trim();
              if (!name) return;
              await onCreateGroup(name);
              setNewGroupName("");
              setGroupFormOpen(false);
            }}
            onSelectAllContacts={() => {
              onSelectAllContacts();
              onClose();
            }}
            onSelectGroup={(groupId) => {
              onSelectGroup(groupId);
              onClose();
            }}
            onRenameGroup={onRenameGroup}
            onDeleteGroup={onDeleteGroup}
            onDeleteAllContacts={onDeleteAllContacts}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
